/**
 * PetHealthPanel — inline health monitoring panel for a pet.
 *
 * Embeds three sections accessed via horizontal tab navigation:
 *   1. Observations — chronological trait chip log + optional notes.
 *   2. Weight — weight history (g → kg), trend arrow, log form.
 *   3. Daily checks — quick YES/NO welfare check stored as a structured
 *      observation note (DAILY_CHECK prefix). Booking-context only.
 *
 * The component fetches its own data. Pass `canWrite` when the current
 * staff member holds `pets.observations.write` / `pets.weights.write`.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import { Activity, ClipboardCheck, Plus, Scale, TrendingDown, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Types (mirrored from API — kept local so no shared package needed)
// ---------------------------------------------------------------------------

interface ObservationEntry {
  id: string;
  petId: string;
  bookingId: string | null;
  traitsJson: string;
  notes: string | null;
  observedByMembershipId: string | null;
  createdAt: string;
}

interface WeightEntry {
  id: string;
  petId: string;
  weightGrams: number;
  weighedAt: string;
  note: string | null;
  recordedByMembershipId: string | null;
  createdAt: string;
}

export interface PetHealthPanelProps {
  petId: string;
  petName: string;
  petSpecies: 'dog' | 'cat' | string;
  bookingId?: string;
  canWrite?: boolean;
}

// ---------------------------------------------------------------------------
// Trait meta — colour coding by category
// ---------------------------------------------------------------------------

const TRAIT_POSITIVE = new Set([
  'friendly',
  'confident',
  'dog_social',
  'affectionate',
  'cat_tolerant',
]);
const TRAIT_CONCERN = new Set([
  'anxious',
  'aggressive',
  'food_protective',
  'resource_guarding',
  'escape_artist',
]);

function traitClass(trait: string): string {
  if (TRAIT_POSITIVE.has(trait))
    return 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200';
  if (TRAIT_CONCERN.has(trait))
    return 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200';
  return 'bg-secondary text-muted-foreground ring-1 ring-inset ring-border';
}

function traitLabel(t: string): string {
  return t.replace(/_/g, ' ');
}

const ALL_TRAITS = [
  'friendly',
  'anxious',
  'aggressive',
  'energetic',
  'reserved',
  'confident',
  'dog_social',
  'cat_tolerant',
  'escape_artist',
  'vocal',
  'quiet',
  'food_protective',
  'resource_guarding',
  'affectionate',
  'independent',
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fmtWeight(grams: number): string {
  return (grams / 1000).toFixed(2);
}

const DAILY_CHECK_PREFIX = 'DAILY_CHECK:';

function parseDailyCheck(notes: string | null): {
  eating: 'yes' | 'no' | null;
  toileting: 'yes' | 'no' | null;
  health: 'ok' | 'concern' | null;
} | null {
  if (!notes?.startsWith(DAILY_CHECK_PREFIX)) return null;
  const body = notes.slice(DAILY_CHECK_PREFIX.length).trim();
  const m = body.match(/eating=(yes|no).*toileting=(yes|no).*health=(ok|concern)/);
  if (!m) return null;
  return {
    eating: m[1] as 'yes' | 'no',
    toileting: m[2] as 'yes' | 'no',
    health: m[3] as 'ok' | 'concern',
  };
}

function buildDailyCheckNote(
  eating: 'yes' | 'no',
  toileting: 'yes' | 'no',
  health: 'ok' | 'concern',
): string {
  return `${DAILY_CHECK_PREFIX} eating=${eating}, toileting=${toileting}, health=${health}`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

type Tab = 'observations' | 'weight' | 'checks';

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
      }`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function PetHealthPanel({
  petId,
  petName,
  petSpecies: _petSpecies,
  bookingId,
  canWrite = false,
}: PetHealthPanelProps) {
  const { state } = useAuthContext();
  const capabilities = state.me?.capabilities ?? [];
  const canWriteObservations =
    canWrite && (capabilities.includes('pets.observations.write') || capabilities.includes('*'));
  const canWriteWeights =
    canWrite && (capabilities.includes('pets.weights.write') || capabilities.includes('*'));

  const [activeTab, setActiveTab] = useState<Tab>('observations');

  // ---- Observations state ----
  const [observations, setObservations] = useState<ObservationEntry[]>([]);
  const [obsLoading, setObsLoading] = useState(true);
  const [obsError, setObsError] = useState<string | null>(null);
  const [addObsOpen, setAddObsOpen] = useState(false);
  const [selectedTraits, setSelectedTraits] = useState<Set<string>>(new Set());
  const [obsNotes, setObsNotes] = useState('');
  const [obsSaving, setObsSaving] = useState(false);
  const [obsSaveError, setObsSaveError] = useState<string | null>(null);

  // ---- Weight state ----
  const [weights, setWeights] = useState<WeightEntry[]>([]);
  const [wtLoading, setWtLoading] = useState(true);
  const [wtError, setWtError] = useState<string | null>(null);
  const [addWtOpen, setAddWtOpen] = useState(false);
  const [wtKg, setWtKg] = useState('');
  const [wtNote, setWtNote] = useState('');
  const [wtSaving, setWtSaving] = useState(false);
  const [wtSaveError, setWtSaveError] = useState<string | null>(null);

  // ---- Daily checks state ----
  const [checkSaving, setCheckSaving] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const loadObservations = useCallback(async () => {
    setObsLoading(true);
    setObsError(null);
    try {
      const endpoint = bookingId
        ? `/api/bookings/${bookingId}/observations`
        : `/api/pets/${petId}/observations`;
      const res = await fetch(endpoint, { credentials: 'include' });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = (await res.json()) as { items: ObservationEntry[] };
      setObservations(data.items);
    } catch (e) {
      setObsError((e as Error).message);
    } finally {
      setObsLoading(false);
    }
  }, [petId, bookingId]);

  const loadWeights = useCallback(async () => {
    setWtLoading(true);
    setWtError(null);
    try {
      const res = await fetch(`/api/pets/${petId}/weight-logs`, { credentials: 'include' });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = (await res.json()) as { items: WeightEntry[] };
      setWeights(data.items);
    } catch (e) {
      setWtError((e as Error).message);
    } finally {
      setWtLoading(false);
    }
  }, [petId]);

  useEffect(() => {
    void loadObservations();
    void loadWeights();
  }, [loadObservations, loadWeights]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  async function submitObservation(e: React.FormEvent) {
    e.preventDefault();
    if (selectedTraits.size === 0) {
      setObsSaveError('Select at least one trait');
      return;
    }
    setObsSaving(true);
    setObsSaveError(null);
    try {
      const endpoint = bookingId
        ? `/api/bookings/${bookingId}/observations`
        : `/api/pets/${petId}/observations`;
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          traits: Array.from(selectedTraits),
          notes: obsNotes.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { issues?: string[] };
        throw new Error(err.issues?.[0] ?? `Error ${res.status}`);
      }
      const { item } = (await res.json()) as { item: ObservationEntry };
      setObservations((prev) => [item, ...prev]);
      setSelectedTraits(new Set());
      setObsNotes('');
      setAddObsOpen(false);
    } catch (err) {
      setObsSaveError((err as Error).message);
    } finally {
      setObsSaving(false);
    }
  }

  async function submitWeight(e: React.FormEvent) {
    e.preventDefault();
    const kg = Number.parseFloat(wtKg);
    if (!Number.isFinite(kg) || kg <= 0) {
      setWtSaveError('Enter a valid weight in kg');
      return;
    }
    const grams = Math.round(kg * 1000);
    if (grams > 500_000) {
      setWtSaveError('Weight exceeds 500 kg — check the value');
      return;
    }
    setWtSaving(true);
    setWtSaveError(null);
    try {
      const res = await fetch(`/api/pets/${petId}/weight-logs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weightGrams: grams, note: wtNote.trim() || null }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { issues?: string[] };
        throw new Error(err.issues?.[0] ?? `Error ${res.status}`);
      }
      const { entry } = (await res.json()) as { entry: WeightEntry };
      setWeights((prev) => [entry, ...prev]);
      setWtKg('');
      setWtNote('');
      setAddWtOpen(false);
    } catch (err) {
      setWtSaveError((err as Error).message);
    } finally {
      setWtSaving(false);
    }
  }

  async function deleteWeight(id: string, petIdParam: string) {
    try {
      const res = await fetch(`/api/pets/${petIdParam}/weight-logs/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      setWeights((prev) => prev.filter((w) => w.id !== id));
    } catch {
      // Silently ignore — rare failure; user can refresh.
    }
  }

  async function submitDailyCheck(
    eating: 'yes' | 'no',
    toileting: 'yes' | 'no',
    health: 'ok' | 'concern',
  ) {
    if (!bookingId) return;
    setCheckSaving(true);
    setCheckError(null);
    try {
      const res = await fetch(`/api/bookings/${bookingId}/observations`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          traits: ['friendly'],
          notes: buildDailyCheckNote(eating, toileting, health),
        }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { issues?: string[] };
        throw new Error(err.issues?.[0] ?? `Error ${res.status}`);
      }
      const { item } = (await res.json()) as { item: ObservationEntry };
      setObservations((prev) => [item, ...prev]);
    } catch (err) {
      setCheckError((err as Error).message);
    } finally {
      setCheckSaving(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const todayChecks = observations.filter((o) => {
    if (!o.notes?.startsWith(DAILY_CHECK_PREFIX)) return false;
    return o.createdAt.slice(0, 10) === todayIso();
  });
  const latestTodayCheck = todayChecks[0] ?? null;
  const parsedTodayCheck = latestTodayCheck ? parseDailyCheck(latestTodayCheck.notes) : null;

  const wtLatest = weights[0];
  const wtPrev = weights[1];
  const wtTrend: 'up' | 'down' | 'stable' | null =
    wtLatest !== undefined && wtPrev !== undefined
      ? wtLatest.weightGrams > wtPrev.weightGrams
        ? 'up'
        : wtLatest.weightGrams < wtPrev.weightGrams
          ? 'down'
          : 'stable'
      : null;

  // Non-daily-check observations to show in the Observations tab.
  const displayObservations = observations.filter((o) => !o.notes?.startsWith(DAILY_CHECK_PREFIX));

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="mt-3 rounded-xl border border-border bg-background">
      {/* Tab bar */}
      <div className="flex items-center gap-1.5 border-b border-border px-3 py-2">
        <TabButton
          active={activeTab === 'observations'}
          onClick={() => setActiveTab('observations')}
        >
          <Activity className="size-3" strokeWidth={2} />
          Observations
        </TabButton>
        <TabButton active={activeTab === 'weight'} onClick={() => setActiveTab('weight')}>
          <Scale className="size-3" strokeWidth={2} />
          Weight
        </TabButton>
        {bookingId && (
          <TabButton active={activeTab === 'checks'} onClick={() => setActiveTab('checks')}>
            <ClipboardCheck className="size-3" strokeWidth={2} />
            Daily check
          </TabButton>
        )}
        <span className="ml-1 text-[11px] text-muted-foreground/60">{petName}</span>
      </div>

      {/* ----- Observations tab ----- */}
      {activeTab === 'observations' && (
        <div className="px-3 py-3">
          {obsLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
          {obsError && <p className="text-xs text-destructive">Failed to load observations</p>}
          {!obsLoading && !obsError && displayObservations.length === 0 && (
            <p className="text-xs text-muted-foreground/70">No observations recorded yet.</p>
          )}
          {!obsLoading && !obsError && displayObservations.length > 0 && (
            <ul className="space-y-2">
              {displayObservations.map((o) => {
                let traits: string[] = [];
                try {
                  traits = JSON.parse(o.traitsJson) as string[];
                } catch {
                  traits = [];
                }
                return (
                  <li
                    key={o.id}
                    className="rounded-lg border border-border/60 bg-surface px-3 py-2"
                  >
                    <div className="flex flex-wrap gap-1">
                      {traits.map((t) => (
                        <span
                          key={t}
                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${traitClass(t)}`}
                        >
                          {traitLabel(t)}
                        </span>
                      ))}
                    </div>
                    {o.notes && <p className="mt-1.5 text-xs text-muted-foreground">{o.notes}</p>}
                    <p className="mt-1 text-[10px] text-muted-foreground/60">
                      {fmtDate(o.createdAt)}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}

          {canWriteObservations && (
            <div className="mt-3">
              {!addObsOpen ? (
                <button
                  type="button"
                  onClick={() => setAddObsOpen(true)}
                  className="flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary"
                >
                  <Plus className="size-3" strokeWidth={2} />
                  Add observation
                </button>
              ) : (
                <form
                  onSubmit={submitObservation}
                  className="mt-1 space-y-2.5 rounded-lg border border-border bg-surface p-3"
                >
                  <p className="text-[10px] font-semibold uppercase text-muted-foreground/60">
                    Select traits
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {ALL_TRAITS.map((t) => {
                      const active = selectedTraits.has(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() =>
                            setSelectedTraits((prev) => {
                              const next = new Set(prev);
                              if (next.has(t)) next.delete(t);
                              else next.add(t);
                              return next;
                            })
                          }
                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize transition-colors ${
                            active
                              ? traitClass(t)
                              : 'bg-background text-muted-foreground ring-1 ring-inset ring-border hover:bg-secondary'
                          }`}
                        >
                          {traitLabel(t)}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    value={obsNotes}
                    onChange={(e) => setObsNotes(e.target.value)}
                    placeholder="Notes (optional)"
                    rows={2}
                    maxLength={2000}
                    className="w-full resize-none rounded border border-border bg-background px-2 py-1.5 text-xs placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  {obsSaveError && <p className="text-xs text-destructive">{obsSaveError}</p>}
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={obsSaving}
                      className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
                    >
                      {obsSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setAddObsOpen(false);
                        setSelectedTraits(new Set());
                        setObsNotes('');
                        setObsSaveError(null);
                      }}
                      className="rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      )}

      {/* ----- Weight tab ----- */}
      {activeTab === 'weight' && (
        <div className="px-3 py-3">
          {wtLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
          {wtError && <p className="text-xs text-destructive">Failed to load weight logs</p>}
          {!wtLoading && !wtError && weights.length === 0 && (
            <p className="text-xs text-muted-foreground/70">No weight records.</p>
          )}
          {!wtLoading && !wtError && weights.length > 0 && (
            <div className="space-y-1">
              {wtTrend && (
                <div className="mb-2 flex items-center gap-1 text-[11px] font-medium">
                  {wtTrend === 'up' && (
                    <span className="flex items-center gap-0.5 text-amber-600">
                      <TrendingUp className="size-3.5" strokeWidth={2} />
                      Weight up from last reading
                    </span>
                  )}
                  {wtTrend === 'down' && (
                    <span className="flex items-center gap-0.5 text-blue-600">
                      <TrendingDown className="size-3.5" strokeWidth={2} />
                      Weight down from last reading
                    </span>
                  )}
                  {wtTrend === 'stable' && (
                    <span className="text-muted-foreground">Weight stable</span>
                  )}
                </div>
              )}
              <ul className="space-y-1">
                {weights.map((w) => (
                  <li
                    key={w.id}
                    className="flex items-center justify-between gap-2 rounded border border-border/50 bg-surface px-2.5 py-1.5 text-xs"
                  >
                    <div className="flex items-center gap-3">
                      <span className="num font-semibold text-foreground">
                        {fmtWeight(w.weightGrams)} kg
                      </span>
                      <span className="text-muted-foreground">{w.weighedAt.slice(0, 10)}</span>
                      {w.note && <span className="italic text-muted-foreground/70">{w.note}</span>}
                    </div>
                    {canWriteWeights && (
                      <button
                        type="button"
                        onClick={() => void deleteWeight(w.id, petId)}
                        className="text-[10px] text-muted-foreground/50 hover:text-destructive"
                        aria-label="Delete weight entry"
                      >
                        &times;
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {canWriteWeights && (
            <div className="mt-3">
              {!addWtOpen ? (
                <button
                  type="button"
                  onClick={() => setAddWtOpen(true)}
                  className="flex items-center gap-1 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary"
                >
                  <Plus className="size-3" strokeWidth={2} />
                  Log weight
                </button>
              ) : (
                <form
                  onSubmit={submitWeight}
                  className="mt-1 flex flex-wrap items-end gap-2 rounded-lg border border-border bg-surface p-3"
                >
                  <div className="space-y-0.5">
                    <label className="text-[10px] font-medium uppercase text-muted-foreground/60">
                      Weight (kg)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={wtKg}
                      onChange={(e) => setWtKg(e.target.value)}
                      placeholder="e.g. 12.5"
                      required
                      className="num w-28 rounded border border-border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-0.5">
                    <label className="text-[10px] font-medium uppercase text-muted-foreground/60">
                      Note (optional)
                    </label>
                    <input
                      type="text"
                      value={wtNote}
                      onChange={(e) => setWtNote(e.target.value)}
                      placeholder="e.g. post-groom"
                      maxLength={500}
                      className="w-36 rounded border border-border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  {wtSaveError && <p className="w-full text-xs text-destructive">{wtSaveError}</p>}
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={wtSaving}
                      className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
                    >
                      {wtSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setAddWtOpen(false);
                        setWtKg('');
                        setWtNote('');
                        setWtSaveError(null);
                      }}
                      className="rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      )}

      {/* ----- Daily checks tab (booking context only) ----- */}
      {activeTab === 'checks' && bookingId && (
        <div className="px-3 py-3">
          {parsedTodayCheck ? (
            <div className="space-y-2">
              <p className="text-[10px] font-semibold uppercase text-muted-foreground/60">
                Today's welfare check
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <CheckResult label="Eating well" value={parsedTodayCheck.eating} />
                <CheckResult label="Normal toileting" value={parsedTodayCheck.toileting} />
                <CheckResult
                  label="No injuries"
                  value={parsedTodayCheck.health === 'ok' ? 'yes' : 'no'}
                />
              </div>
              <p className="text-[10px] text-muted-foreground/60">
                Recorded {fmtDate(latestTodayCheck?.createdAt ?? '')}
              </p>
              {canWriteObservations && (
                <p className="text-[10px] text-muted-foreground/50">
                  To update, record a new check below — the latest replaces this one.
                </p>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground/70">No welfare check recorded today.</p>
          )}

          {canWriteObservations && (
            <DailyCheckForm saving={checkSaving} error={checkError} onSubmit={submitDailyCheck} />
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Daily check sub-components
// ---------------------------------------------------------------------------

function CheckResult({ label, value }: { label: string; value: 'yes' | 'no' | null }) {
  return (
    <div className="flex flex-col gap-0.5 rounded border border-border/60 bg-surface p-2">
      <p className="text-[10px] text-muted-foreground">{label}</p>
      {value === 'yes' && <span className="text-xs font-semibold text-emerald-600">Yes</span>}
      {value === 'no' && <span className="text-xs font-semibold text-amber-600">No</span>}
      {value === null && <span className="text-xs text-muted-foreground/60">—</span>}
    </div>
  );
}

function DailyCheckForm({
  saving,
  error,
  onSubmit,
}: {
  saving: boolean;
  error: string | null;
  onSubmit: (
    eating: 'yes' | 'no',
    toileting: 'yes' | 'no',
    health: 'ok' | 'concern',
  ) => Promise<void>;
}) {
  const [eating, setEating] = useState<'yes' | 'no'>('yes');
  const [toileting, setToileting] = useState<'yes' | 'no'>('yes');
  const [health, setHealth] = useState<'ok' | 'concern'>('ok');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void onSubmit(eating, toileting, health);
      }}
      className="mt-3 rounded-lg border border-border bg-surface p-3 space-y-3"
    >
      <p className="text-[10px] font-semibold uppercase text-muted-foreground/60">
        Record today's check
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <YesNoToggle label="Eating well" value={eating} onChange={setEating} />
        <YesNoToggle label="Normal toileting" value={toileting} onChange={setToileting} />
        <div>
          <p className="mb-1 text-[10px] text-muted-foreground">No injuries/illness</p>
          <div className="flex gap-1">
            {(['ok', 'concern'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setHealth(v)}
                className={`rounded px-2 py-0.5 text-[10px] font-medium ${
                  health === v
                    ? v === 'ok'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                    : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                }`}
              >
                {v === 'ok' ? 'OK' : 'Concern'}
              </button>
            ))}
          </div>
        </div>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <button
        type="submit"
        disabled={saving}
        className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save check'}
      </button>
    </form>
  );
}

function YesNoToggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: 'yes' | 'no';
  onChange: (v: 'yes' | 'no') => void;
}) {
  return (
    <div>
      <p className="mb-1 text-[10px] text-muted-foreground">{label}</p>
      <div className="flex gap-1">
        {(['yes', 'no'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={`rounded px-2 py-0.5 text-[10px] font-medium capitalize ${
              value === v
                ? v === 'yes'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-amber-100 text-amber-700'
                : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
            }`}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}
